import type { Stop } from '@day';
import { dayOf } from '@day';
import { dayWords, time } from '../app/words';
import { writtenOf } from './routeSheet';

/**
 * Share the proof (proof/share-proof, the phone's proof_share_sheet.dart): a delivered stop's photo and what happened, as
 * one picture for WhatsApp. Nothing of Google's goes in it: the address as it was written (`writtenOf`, as the route
 * sheet and the phone's export), never Google's wording, a position or a map; the courier by their own first name, as
 * customers know them.
 */

export interface ProofWords {
  photo: string;
  /** "Delivered" or "Picked up". */
  what: string;
  /** When, for whom, where, by whom. */
  lines: string[];
  business: string | null;
  fileName: string;
}

/** What the picture says, for a delivered stop with a photo; null for anything else. */
export function proofWords(s: Stop, o: { zone: string; business?: string | null; courier?: string | null }): ProofWords | null {
  const photo = s.photos.find((u) => /^https?:\/\//.test(u) || u.startsWith('/'));
  if (s.state !== 'done' || !photo || s.handover) return null;
  const pickup = s.type === 'pickup';
  const address = writtenOf(s);
  const first = o.courier?.trim().split(/\s+/)[0];
  const by = first && !first.startsWith('+') ? first : null;
  const lines = [
    ...(s.at != null ? [`${dayWords(dayOf(s.at, o.zone), { weekday: true, short: true })} · ${time(s.at, o.zone)}`] : []),
    ...(s.name ? [`${pickup ? 'From' : 'To'} ${s.name}`] : []),
    address ?? `Stop ${s.n}`,
    ...(by ? [`${pickup ? 'Picked up' : 'Delivered'} by ${by}`] : []),
  ];
  const slug = (address ?? `stop ${s.n}`).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/, '');
  return { photo, what: pickup ? 'Picked up' : 'Delivered', lines, business: o.business?.trim() || null, fileName: `proof-${slug || 'delivery'}.png` };
}

/** The shared pictures' colours, whatever the page's theme (ShareInk: the light chrome, the dark ink). */
const INK = { canvas: '#15130F', ink: '#F4F1EA', muted: '#A8A296', soft: '#6F6A60', positive: '#34C77B' };

/** The photo couldn't be drawn: not reachable, or its storage doesn't let this page read it (CORS). */
export class PhotoUnreadable extends Error {
  constructor() {
    super('photo-unreadable');
  }
}

function load(src: string, cors: boolean): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (cors) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new PhotoUnreadable());
    img.src = src;
  });
}

/** Lines of [text] no wider than [max] at the canvas's font. */
function wrap(ctx: CanvasRenderingContext2D, text: string, max: number): string[] {
  const out: string[] = [];
  let line = '';
  for (const word of text.split(/\s+/)) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width <= max || !line) line = next;
    else {
      out.push(line);
      line = word;
    }
  }
  if (line) out.push(line);
  return out;
}

/**
 * The picture, 1080 px wide (the phone's card at 3×): the photo, square, then the business, Delivered and the lines, and
 * "Delivered with Routr". [mark] is the Routr icon (same origin). Throws [PhotoUnreadable] when the photo can't be drawn.
 */
export async function proofPicture(w: ProofWords, mark?: string): Promise<Blob> {
  await document.fonts?.ready;
  const sameOrigin = w.photo.startsWith('/') || new URL(w.photo, location.href).origin === location.origin;
  const photo = await load(w.photo, !sameOrigin);
  const W = 1080;
  const pad = 60;
  const side = W - pad * 2;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  // The text's height first, from its lines at their sizes.
  ctx.font = '600 45px "Plus Jakarta Sans"';
  const wrapped = w.lines.flatMap((l) => wrap(ctx, l, side));
  const H = pad + side + 48 + (w.business ? 54 : 0) + 84 + wrapped.length * 60 + 42 + 66 + pad - 6;
  canvas.width = W;
  canvas.height = H;
  ctx.fillStyle = INK.canvas;
  ctx.fillRect(0, 0, W, H);
  // The photo, filling its square.
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(pad, pad, side, side, 42);
  ctx.clip();
  const k = Math.max(side / photo.naturalWidth, side / photo.naturalHeight) || 1;
  const pw = photo.naturalWidth * k;
  const ph = photo.naturalHeight * k;
  ctx.drawImage(photo, pad + (side - pw) / 2, pad + (side - ph) / 2, pw, ph);
  ctx.restore();
  let y = pad + side + 48;
  ctx.textBaseline = 'top';
  if (w.business) {
    ctx.fillStyle = INK.muted;
    ctx.font = '600 42px "Plus Jakarta Sans"';
    ctx.fillText(w.business, pad, y);
    y += 54;
  }
  // Green for delivered, as everywhere; amber stays Routr's.
  ctx.fillStyle = INK.positive;
  ctx.beginPath();
  ctx.arc(pad + 30, y + 36, 30, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = INK.canvas;
  ctx.lineWidth = 7;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(pad + 17, y + 37);
  ctx.lineTo(pad + 26, y + 46);
  ctx.lineTo(pad + 44, y + 27);
  ctx.stroke();
  ctx.fillStyle = INK.ink;
  ctx.font = '700 72px "Bricolage Grotesque"';
  ctx.fillText(w.what, pad + 84, y);
  y += 84 + 12;
  ctx.font = '600 45px "Plus Jakarta Sans"';
  for (const l of wrapped) {
    ctx.fillText(l, pad, y);
    y += 60;
  }
  y += 30;
  if (mark) {
    try {
      const m = await load(mark, false);
      ctx.drawImage(m, pad, y, 66, 66);
    } catch {
      // The words alone.
    }
  }
  ctx.fillStyle = INK.muted;
  ctx.font = '600 39px "Plus Jakarta Sans"';
  ctx.fillText('Delivered with Routr', pad + (mark ? 90 : 0), y + 12);
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new PhotoUnreadable())), 'image/png');
    } catch {
      // A photo from storage that doesn't allow this page (CORS) leaves the canvas unreadable.
      reject(new PhotoUnreadable());
    }
  });
}
